import React, { useState, useEffect } from 'react';
import { commissionsApi } from '../services/api';
import '../styles/agencies.css';
import { extractErrorMessage } from '../utils/errorMessage';
import { useTranslation } from 'react-i18next';

interface Tier {
  model_type: string;
  min_spend: number;
  commission_percent: number;
}

type SectionType = 'own_accounts' | 'client_accounts';

// `key` is the stored model_type — only the keys beside it are interface text.
const SECTIONS: { key: SectionType; labelKey: string; descKey: string; icon: React.ReactNode }[] = [
  {
    key: 'own_accounts',
    labelKey: 'tiers.secOwnAccounts',
    descKey: 'tiers.secOwnAccountsDesc',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  },
  {
    key: 'client_accounts',
    labelKey: 'tiers.secClientAccounts',
    descKey: 'tiers.secClientAccountsDesc',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11h20V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5z"/></svg>,
  },
];

const CommissionTiersPage: React.FC = () => {
  const { t } = useTranslation();
  const [tiersByType, setTiersByType] = useState<Record<string, Tier[]>>({
    own_accounts: [],
    client_accounts: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeSection, setActiveSection] = useState<SectionType>('own_accounts');

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const data = await commissionsApi.getTiers();
      setTiersByType({
        own_accounts: data.tiers['own_accounts'] || [],
        client_accounts: data.tiers['client_accounts'] || [],
      });
    } catch (err: any) {
      setError(extractErrorMessage(err, t('tiers.errLoadCommission')));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const allTiers = [
        ...tiersByType.own_accounts.map(t => ({ ...t, model_type: 'own_accounts' })),
        ...tiersByType.client_accounts.map(t => ({ ...t, model_type: 'client_accounts' })),
      ];
      await commissionsApi.updateTiers(allTiers);
      setSuccess(t('tiers.savedCommission'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err, t('tiers.errSaveCommission')));
    } finally {
      setSaving(false);
    }
  };

  const tiers = tiersByType[activeSection] || [];

  const setTiers = (newTiers: Tier[]) => {
    setTiersByType(prev => ({ ...prev, [activeSection]: newTiers }));
  };

  const addTier = () => {
    setTiers([...tiers, { model_type: activeSection, min_spend: 0, commission_percent: 0 }]);
  };

  const removeTier = (index: number) => {
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const updateTier = (index: number, field: 'min_spend' | 'commission_percent', value: number) => {
    const updated = [...tiers];
    updated[index] = { ...updated[index], [field]: value };
    setTiers(updated);
  };

  const formatNumber = (n: number): string => {
    if (!n) return '0';
    return n.toLocaleString('ru-RU');
  };

  const parseFormattedNumber = (s: string): number => {
    const cleaned = s.replace(/[^\d]/g, '');
    return parseInt(cleaned, 10) || 0;
  };

  const section = SECTIONS.find(s => s.key === activeSection)!;

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('tiers.commissionTitle')}</h1>
          <p className="header-subtitle">{t('tiers.commissionSubtitle')}</p>
        </div>
      </div>

      <div className="url-lists-layout">
        {/* Left Panel */}
        <aside className="url-lists-panel">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              className={`settings-nav-item ${activeSection === s.key ? 'active' : ''}`}
              onClick={() => setActiveSection(s.key)}
            >
              {s.icon}
              {t(s.labelKey)}
            </button>
          ))}
        </aside>

        {/* Editor Area */}
        <div className="url-editor-panel">
          {loading ? (
            <div className="offices-loading">
              <div className="offices-spinner" />
            </div>
          ) : (
            <div className="settings-content">
              {error && (
                <div className="settings-message settings-message-error">
                  {error}
                  <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>&times;</button>
                </div>
              )}
              {success && (
                <div className="settings-message settings-message-success">{success}</div>
              )}

              <div className="settings-card">
                <h2 className="settings-card-title">{t(section.labelKey)}</h2>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 24px 0' }}>
                  {t(section.descKey)}
                </p>

                {tiers.map((tier, i) => (
                  <div key={i} className="discount-tier-row">
                    <div className="settings-form-group" style={{ flex: 1, marginBottom: 0 }}>
                      {i === 0 && <label className="field-label">{t('tiers.minSpend')}</label>}
                      <input
                        type="text"
                        className="settings-input"
                        value={formatNumber(tier.min_spend)}
                        onChange={e => updateTier(i, 'min_spend', parseFormattedNumber(e.target.value))}
                      />
                    </div>
                    <div className="settings-form-group" style={{ width: '120px', flexShrink: 0, marginBottom: 0 }}>
                      {i === 0 && <label className="field-label">{t('tiers.commissionPercent')}</label>}
                      <input
                        type="number"
                        className="settings-input"
                        min={0}
                        max={50}
                        step={0.5}
                        value={tier.commission_percent}
                        onChange={e => updateTier(i, 'commission_percent', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <button
                      className="discount-tier-remove"
                      onClick={() => removeTier(i)}
                      title={t('tiers.removeTier')}
                      style={i === 0 ? { marginTop: '22px' } : undefined}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                ))}

                {tiers.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {t('tiers.noTiers')}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button className="discount-tier-add" onClick={addTier}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    {t('tiers.addTier')}
                  </button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button
                    className="editor-empty-btn"
                    onClick={handleSave}
                    disabled={saving}
                    style={{ padding: '14px 48px' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {t(saving ? 'tiers.saving' : 'common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommissionTiersPage;
